import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Divider,
  Switch,
  Badge,
  Tooltip,
  Popover,
  Typography,
  Button,
  Avatar,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ArrowDropDownCircleOutlinedIcon from "@mui/icons-material/ArrowDropDownCircleOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import PersonIcon from "@mui/icons-material/Person";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CloseIcon from "@mui/icons-material/Close";
import NameLogo from "src/@core/components/icons/Name-Logo";
import IconLogo from "src/@core/components/icons/Icon-Logo";
import Logo from "src/@core/components/logo";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuthModal } from "src/context/AuthModalContext";

import BallotOutlinedIcon from "@mui/icons-material/BallotOutlined";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import HomeIcon from "src/@core/components/icons/HomeIcon";
import UserCircle from "src/@core/components/icons/UserIcon";
import {
  logout,
  setIsShowSetupMenu,
  setShowSubscriptionPlanDialog,
  setShowBusinessDeactivationDialog,
  switchProfile,
} from "src/redux/slices/AuthSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchBusinessSubscription } from "src/redux/actions/AuthAction";
import { IMAGE_URL, MEDIA_URL, ApiEndPoints, getImageDisplayUrl } from "src/network/endpoints";
import ExchangeCircle from "src/@core/components/icons/ExchangeIcon";
import files from "src/utils/constants";
import ApprovIcon from "../../@core/components/icons/ApprovIcon";
import ShieldIcon from "@mui/icons-material/Shield";
import NotificationDropdown from "./notification";
import { useQuery } from "react-query";
import { axiosInstance } from "src/network/adapter";
import socketService from "src/services/socketService";
import { useTranslation } from "react-i18next";
import { useSettings } from "src/@core/hooks/useSettings";
import LanguageDropdown from "src/@core/layouts/components/shared-components/LanguageDropdown";

// Constants
const SIDEBAR_WIDTH_EXPANDED = 350;

// Routes that should use Dialog instead of Sidebar
const DIALOG_ROUTES = ["/dashboard", "/", "/chats"];

// Styled components
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "topOffset",
})(({ theme, topOffset = 0 }) => ({
  backgroundColor: "#6C00E6",
  color: "#fff",
  boxShadow: "none",
  justifyContent: "center",
  position: "fixed",
  top: topOffset,
  zIndex: theme.zIndex.drawer + 1,
}));

const LogoBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 10,
});

const DividerVertical = styled("div")({
  height: 28,
  borderLeft: "1px solid #fff",
  margin: "0 10px",
  opacity: 0.5,
});

const CenterMenuBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 20,
  flexGrow: 1,
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    gap: 0,
    justifyContent: "space-evenly",
  },
}));

const RightMenuBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 20,
});

// Sidebar Component
const Sidebar = styled(Box)(({ theme, isExpanded }) => ({
  position: "fixed",
  left: isExpanded ? 0 : -SIDEBAR_WIDTH_EXPANDED,
  top: 0,
  height: "100vh",
  width: SIDEBAR_WIDTH_EXPANDED,
  backgroundColor: "#fff",
  paddingTop: 64,
  transition: "left 0.3s ease-in-out",
  zIndex: theme.zIndex.drawer,
  overflow: "hidden",
  boxShadow: "2px 0 10px rgba(0,0,0,0.1)",
}));

const CustomScrollBox = styled(Box)(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
    display: "none",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#6C00E6",
    borderRadius: "10px",
    "&:hover": {
      background: "#5500b3",
    },
  },
  "&::-webkit-scrollbar-button": {
    display: "none",
  },
  "&": {
    "&::-webkit-scrollbar-thumb": {
      visibility: "hidden",
    },
    "&:hover::-webkit-scrollbar-thumb": {
      visibility: "visible",
    },
    "&::-webkit-scrollbar-thumb:window-inactive": {
      visibility: "hidden",
    },
  },
  scrollbarWidth: "thin",
  scrollbarColor: "#6C00E6 transparent",
}));

const SidebarContent = styled(CustomScrollBox)({
  padding: "16px 0",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    position: "fixed",
    left: "24px",
    top: "42px",
    width: SIDEBAR_WIDTH_EXPANDED,
    margin: 0,
    borderRadius: "20px",
    maxWidth: "none",
    backgroundColor: "#fff",
    [theme.breakpoints.down("sm")]: {
      left: 0,
      top: 0,
    },
    boxShadow: "2px 0 10px rgba(0,0,0,0.1)",
    "& .MuiDialogContent-root": {
      "&::-webkit-scrollbar": {
        width: "4px",
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent",
        display: "none",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#6C00E6",
        borderRadius: "10px",
        "&:hover": {
          background: "#5500b3",
        },
      },
      "&::-webkit-scrollbar-button": {
        display: "none",
      },
      "&": {
        "&::-webkit-scrollbar-thumb": {
          visibility: "hidden",
        },
        "&:hover::-webkit-scrollbar-thumb": {
          visibility: "visible",
        },
        "&::-webkit-scrollbar-thumb:window-inactive": {
          visibility: "hidden",
        },
      },
      scrollbarWidth: "thin",
      scrollbarColor: "#6C00E6 transparent",
    },
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.3)",
  },
}));

const DialogHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 20px",
  borderBottom: "1px solid #e0e0e0",
});

const ColoredIconBox = styled(Box)(({ bgcolor }) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundColor: bgcolor,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: 12,
  "& svg": {
    fontSize: 18,
    color: "#fff",
  },
}));

// Dynamic profile menu items function (t = dashboard namespace t function)
const getProfileMenuItems = (user, profileType, selectedBusinessId, currentPath, t) => {
  const items = [];

  // Personal Profile
  items.push({
    label: user?.fullName || user?.name || (t ? t('personalProfile') : 'personal profile'),
    icon: <PersonIcon />,
    path: `/profile/${user?.userId || ""}`,
    color: "#E91E63",
    type: "personal",
    isSelected: profileType === "personal" && currentPath?.startsWith('/profile/'),
    profileData: user,
  });

  // Business Profiles from employee array
  if (user?.employee && user.employee.length > 0) {
    user.employee.forEach((employee) => {
      // Normalize employee data to match business structure
      const businessData = {
        _id: employee?.businessId?._id || employee?._id,
        businessName: employee?.businessName || (t ? t('businessProfile') : 'business profile'),
        primaryLocationId: employee?.primaryLocationId || employee?.locationIds?.[0]?._id || "",
        businessProfilePic: employee?.businessProfilePic || employee?.businessId?.businessProfilePic,
        isApprove: employee?.isApprove ?? employee?.businessId?.isApprove ?? 0,
        isDocumentVerified: employee?.isDocumentVerified ?? employee?.businessId?.isDocumentVerified ?? -1,
        subscriptionStatus: employee?.businessId?.subscriptionStatus || "inactive",
        subscriptionPlan: employee?.subscriptionPlan,
        isActive: employee?.businessId?.isActive ?? true, // Add isActive field from businessId
      };

      items.push({
        label: businessData.businessName,
        icon: <BusinessIcon />,
        path: `/business-dashboard/${businessData.primaryLocationId}`,
        color: "#FF5722",
        type: "business",
        isSelected: profileType === "business" && selectedBusinessId === businessData._id,
        profileData: businessData,
      });
    });
  }

  // Add register business for all profiles
  items.push({
    label: t ? t('registerBusiness') : 'register a business',
    icon: <BusinessIcon />,
    path: "/register-business",
    color: "#FF5722",
    type: "common",
  });

  // Removed setup account - now handled from TeamDashboard

  return items;
};

const getAdditionalMenuItems = (user, profileType, selectedBusinessId, currentPath, t) => {
  const items = [];
  if (!t) return items;

  // ✅ Video menu at the very top for all profiles
  if (profileType === "business") {
    items.push({
      label: t('howToVideos'),
      icon: <OndemandVideoIcon />,
      path: "/tutorial-videos/howto",
      color: "#6C00E6",
      isSelected: currentPath.startsWith("/tutorial-videos"),
    });
  } else {
    items.push({
      label: t('tutorialVideos'),
      icon: <PlayCircleOutlineIcon />,
      path: "/tutorial-videos/demo",
      color: "#6C00E6",
      isSelected: currentPath.startsWith("/tutorial-videos"),
    });
  }

  // ✅ "request" at top for personal users
  if (profileType === "personal") {
    items.push(
      {
        label: t('personalProfileSettings'),
        icon: <PersonIcon />,
        path: "/profile-settings",
        color: "#E91E63",
        isSelected: currentPath === "/profile-settings",
      },
      {
        label: t('trustLevel'),
        icon: <ShieldIcon />,
        path: "/trust-settings",
        color: "#4CAF50",
        isSelected: currentPath === "/trust-settings",
      },
      {
        label: t('appSettings'),
        icon: <PersonIcon />,
        path: "/permissions",
        color: "#FF9800",
        isSelected: currentPath === "/permissions",
      },
      {
        label: t('request'),
        icon: <RequestQuoteIcon />,
        path: "/dashboard/request",
        color: "#9C27B0",
        isSelected: currentPath === "/dashboard/request",
      },
      {
        label: t('myContacts'),
        icon: <GroupIcon />,
        path: "/my-contacts",
        color: "#2196F3",
        isSelected: currentPath === "/my-contacts",
      },
      {
        label: t('myOrders'),
        icon: <ShoppingCartIcon />,
        path: "/my-orders",
        color: "#607D8B",
        isSelected: currentPath === "/my-orders",
      }
    );

    // ✅ Add business orders only for businesses with an active subscription
    if (user?.employee && user.employee.length > 0) {
      user.employee.forEach((employee) => {
        const subscriptionStatus = employee?.businessId?.subscriptionStatus || "inactive";
        if (subscriptionStatus !== "active") return; // Only show orders when business is subscribed

        // Normalize employee data to match business structure
        const businessData = {
          _id: employee?.businessId?._id || employee?._id,
          businessName: employee?.businessName || t('businessProfile'),
          primaryLocationId: employee?.primaryLocationId || employee?.locationIds?.[0]?._id || "",
          businessProfilePic: employee?.businessProfilePic || employee?.businessId?.businessProfilePic,
          isApprove: employee?.isApprove ?? employee?.businessId?.isApprove ?? 0,
          isDocumentVerified: employee?.isDocumentVerified ?? employee?.businessId?.isDocumentVerified ?? -1,
          subscriptionStatus,
          subscriptionPlan: employee?.subscriptionPlan,
          isActive: employee?.businessId?.isActive ?? true,
        };

        items.push({
          label: `${businessData.businessName} ${t('businessOrders')}`,
          icon: <BusinessIcon />,
          path: `/business-orders/${businessData._id}`,
          color: "#FF5722",
          type: "personal",
          isSelected: currentPath === `/business-orders/${businessData._id}`,
          profileData: businessData,
        });
      });
    }
  }

  // ✅ Add "team leave requests" only if business
  if (profileType === "business") {
    const teamLeavePath = selectedBusinessId 
      ? `/dashboard/${selectedBusinessId}/team-leave-request`
      : "/dashboard/team-leave-request";
    items.push(
      {
        label: t('teamLeaveRequests'),
        icon: <ExitToAppIcon />,
        path: teamLeavePath,
        color: "#4CAF50",
        isSelected: currentPath === teamLeavePath,
      },
      {
        label: t('businessContacts'),
        icon: <GroupIcon />,
        path: "/dashboard/business-contact",
        color: "#E91E63",
        isSelected: currentPath === "/dashboard/business-contact",
      }
    );
  }

  // ✅ Common items
  items.push(
    {
      label: t('reportAnIssue'),
      icon: <ReportProblemIcon />,
      path: "/report",
      color: "#FFC107",
      isSelected: currentPath === "/report",
    },
    {
      label: t('notifications'),
      icon: <NotificationsIcon />,
      path: "/notifications",
      isSelected: currentPath === "/notifications",
      color: "#E91E63",
      badge: 1,
    },
    {
      label: t('account'),
      icon: <PersonIcon />,
      path: "/account",
      isSelected: currentPath === "/account",
      color: "#2196F3",
    },
    {
      label: t('contactUs'),
      icon: <ContactSupportIcon />,
      path: "/contact",
      isSelected: currentPath === "/contact",
      color: "#FF9800",
    }
  );

  return items;
};

// Sidebar Menu Content Component (reusable for both sidebar and dialog)
const SidebarMenuContent = ({ onItemClick, user }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation("dashboard");
  const profileType = useSelector((state) => state.auth.profileType);
  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );

  // const primaryLocationId = useSelector((state) => state.auth.primaryLocationId);

  const handleMenuItemClick = (item) => {
    if (item.path.includes(`/profile`) && (!user || !user.userId)) {
      toast.error(t("userNotFound"));
      return;
    }

    if (item.path === "/setup-account") {
      dispatch(setIsShowSetupMenu(true));
      if (onItemClick) onItemClick();
      return;
    }

    // Handle profile switching with business data
    // Only handle profile switching for items from profile section, not additional section
    // Business items in additional section should just navigate without switching
    // Check if path starts with /business-orders to identify additional section business items
    const isAdditionalSectionBusiness = item.type === "business" && item.path?.startsWith("/business-orders");
    
    if (item.type === "personal" || (item.type === "business" && !isAdditionalSectionBusiness)) {
      // If current type is different from selected type
      if (
        item.type !== profileType ||
        (item.type === "business" &&
          item.profileData?._id !== selectedBusinessId)
      ) {
        if (item.type === "business") {
          // Check isActive first - if business is inactive, show deactivation dialog
          const isBusinessActive = item.profileData.isActive !== false; // Default to true if not set
          
          if (!isBusinessActive) {
            // Show deactivation dialog if business is inactive
            dispatch(setShowBusinessDeactivationDialog({
              show: true,
              businessId: item.profileData?._id,
            }));
            if (onItemClick) onItemClick();
            return;
          }
          
          // Check subscription status - if subscription is inactive, show subscription dialog
          const isSubscriptionActive = item.profileData.subscriptionStatus == "active" || 
                                      item.profileData.subscriptionStatus == "trial" || 
                                      item.profileData.subscriptionStatus == "suspended";
          
          if (isSubscriptionActive) {
            // Switch profile first, then navigate
            dispatch(
              switchProfile({
                type: item.type,
                businessId: item.profileData?._id,
                primaryLocationId: item.profileData?.primaryLocationId,
              })
            );
            // Pre-fetch business subscription data to optimize limit checks
            if (item.profileData?._id) {
              dispatch(fetchBusinessSubscription(item.profileData._id));
            }
            // Use setTimeout to ensure Redux state is updated before navigation
            setTimeout(() => {
              navigate(item.path);
              if (onItemClick) onItemClick();
            }, 0);
            return;
          } else {
            // Show subscription dialog if subscription is inactive
            dispatch(setShowSubscriptionPlanDialog({
              show: true,
              businessId: item.profileData?._id,
              currentPlan: item.profileData?.subscriptionPlan?.planName || item.profileData?.planName || null,
            }));
            if (onItemClick) onItemClick();
            return;
          }
        } else {
          // Handle personal profile switching
          dispatch(
            switchProfile({
              type: item.type,
              businessId: null,
              primaryLocationId: null,
            })
          );
          // Use setTimeout to ensure Redux state is updated before navigation
          setTimeout(() => {
            navigate(item.path);
            if (onItemClick) onItemClick();
          }, 0);
          return;
        }
      }
    }

    // Navigate for non-profile items, business items from additional section, or when profile is already selected
    navigate(item.path);
    // Don't close sidebar for business items from additional section
    if (onItemClick && !isAdditionalSectionBusiness) onItemClick();
  };

  const renderMenuItem = (item, index) => {
    const isSelected = item.isSelected || false;

    return (
      <ListItem
        key={`${item.label}-${index}`}
        disablePadding
        sx={{
          mb: 2,
          bgcolor: isSelected ? "rgba(108, 0, 230, 0.08)" : "#F3F5F9",
          borderRadius: 1,
          border: isSelected ? "1px solid #6C00E6" : "1px solid transparent",
        }}
      >
        <ListItemButton
          onClick={() => handleMenuItemClick(item)}
          sx={{
            color: "#333",
            borderRadius: 2,
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
          }}
        >
          {(item.type === "personal" || item.type === "business") && (
            <Avatar
              src={
                item.type === "personal"
                  ? getImageDisplayUrl(item.profileData?.profilePic)
                  : getImageDisplayUrl(item.profileData?.businessProfilePic)
              }
              sx={{ width: 32, height: 32, mr: 2 }}
            />
          )}
          {!(item.type === "personal" || item.type === "business") && (
            <ColoredIconBox bgcolor={item.color}>{item.icon}</ColoredIconBox>
          )}
          <ListItemText
            primary={
              item.type === "business" ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {item.label}
                  <ApprovIcon
                    color={
                      item.profileData?.isApprove === 1 &&
                      item.profileData?.isDocumentVerified === 1
                        ? "#0d6efd"
                        : "#ffc107"
                    }
                    size={20}
                  />
                </Box>
              ) : (
                item.label
              )
            }
            sx={{
              "& .MuiListItemText-primary": {
                fontSize: 14,
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? "#6C00E6" : "#333",
              },
            }}
          />
          {item.badge && (
            <Badge
              badgeContent={item.badge}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: 10,
                  height: 16,
                  minWidth: 16,
                },
              }}
            />
          )}
          {isSelected && (
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                bgcolor: "#6C00E6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ml: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: "white",
                }}
              />
            </Box>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{ px: 1, mb: 2, color: "#666", fontSize: 16, fontWeight: 600 }}
      >
        {t("profileSection")}
      </Typography>
      {getProfileMenuItems(user, profileType, selectedBusinessId, location.pathname, t).map(
        (item, index) => renderMenuItem(item, index)
      )}

      <Divider sx={{ my: 3 }} />

      <Typography
        variant="subtitle2"
        sx={{ px: 1, mb: 2, color: "#666", fontSize: 16, fontWeight: 600 }}
      >
        {t("additionalSection")}
      </Typography>
      {getAdditionalMenuItems(user, profileType, selectedBusinessId, location.pathname, t).map((item, index) =>
        renderMenuItem(item, index)
      )}
    </Box>
  );
};

const PublicLayoutAppBar = ({
  onSidebarToggle,
  useSidebarDialog = false,
  topOffset = 0,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = !!user;
  // console.log("user===>", user);
  const profileType = useSelector((state) => state.auth.profileType);
  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );
  const primaryLocationId = useSelector(
    (state) => state.auth.primaryLocationId
  );

  const selectedBusiness = useSelector((state) => {
    const selectedId = state.auth.selectedBusinessId;
    if (!selectedId) return null;
    
    // Find in employee array
    const employee = state.auth.user?.employee?.find(
      (emp) => emp?.businessId?._id === selectedId || emp?._id === selectedId
    );
    
    if (employee) {
      // Normalize employee data to match business structure
      return {
        _id: employee?.businessId?._id || employee?._id,
        businessName: employee?.businessName,
        primaryLocationId: employee?.primaryLocationId || employee?.locationIds?.[0]?._id,
        businessProfilePic: employee?.businessProfilePic || employee?.businessId?.businessProfilePic,
        isApprove: employee?.isApprove ?? employee?.businessId?.isApprove,
        isDocumentVerified: employee?.isDocumentVerified ?? employee?.businessId?.isDocumentVerified,
      };
    }
    
    return null;
  });

  const { t } = useTranslation(["nav", "dashboard", "auth"]);
  const { settings, saveSettings } = useSettings();
  const menuItems = [
    { label: "home", icon: <HomeIcon />, path: "/home", color: "#6C00E6" },
    {
      label: "post",
      icon: <BallotOutlinedIcon />,
      path: "/post",
      color: "#6C00E6",
    },
    {
      label: "chat",
      icon: <ChatOutlinedIcon />,
      path: "/chats",
      color: "#6C00E6",
    },
    {
      label: "events",
      icon: <EventAvailableOutlinedIcon />,
      path: "/events",
      color: "#6C00E6",
    },
    {
      label: "profile",
      icon: <UserCircle />,
      path: (user) =>
        profileType === "business"
          ? `/business-profile/${primaryLocationId || ""}`
          : `/profile/${user?.userId || ""}`,
      color: "#6C00E6",
    },
  ];

  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [switchAccountAnchorEl, setSwitchAccountAnchorEl] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [unreadConversationsCount, setUnreadConversationsCount] = useState(0);
  
  const { openLoginModal } = useAuthModal();

  const shouldUseDialog =
    DIALOG_ROUTES.includes(location.pathname) || useSidebarDialog;

  // Auto-select business from URL when on business-dashboard route ONLY
  useEffect(() => {
    // Don't auto-switch if we're on personal profile - respect user's choice
    if (profileType === "personal") {
      return;
    }
    
    // Only run this effect on business-dashboard routes, not on profile or other routes
    const businessDashboardMatch = location.pathname.match(/^\/business-dashboard\/([^/]+)/);
    
    if (!businessDashboardMatch) {
      return; // Exit early if not on business-dashboard route
    }
    
    if (!user?.employee) {
      return; // Exit early if no user or employee data
    }
    
    const urlBusinessId = businessDashboardMatch[1];
    
    // Only switch if the URL business ID is different from currently selected business ID
    if (urlBusinessId === selectedBusinessId) {
      return; // Already on the correct business
    }
    
    // Find the business in user's employee array
    const matchingEmployee = user.employee.find(
      (emp) => 
        emp?.businessId?._id === urlBusinessId || 
        emp?._id === urlBusinessId ||
        emp?.primaryLocationId === urlBusinessId ||
        emp?.locationIds?.[0]?._id === urlBusinessId
    );
    
    if (!matchingEmployee) {
      return; // No matching employee found
    }
    
    // Normalize employee data to match business structure
    const businessData = {
      _id: matchingEmployee?.businessId?._id || matchingEmployee?._id,
      businessName: matchingEmployee?.businessName || matchingEmployee?.businessId?.businessName,
      primaryLocationId: matchingEmployee?.primaryLocationId || matchingEmployee?.locationIds?.[0]?._id || "",
      businessProfilePic: matchingEmployee?.businessProfilePic || matchingEmployee?.businessId?.businessProfilePic,
      subscriptionStatus: matchingEmployee?.businessId?.subscriptionStatus || matchingEmployee?.subscriptionStatus || "inactive",
      isActive: matchingEmployee?.businessId?.isActive ?? true,
    };
    
    // Check isActive first - if business is inactive, don't auto-switch
    if (businessData.isActive === false) {
      return; // Don't auto-switch if business is inactive
    }
    
    // Check subscription status
    const isSubscriptionActive = businessData.subscriptionStatus === "active" || 
                                businessData.subscriptionStatus === "trial" || 
                                businessData.subscriptionStatus === "suspended";
    
    if (!isSubscriptionActive) {
      return; // Don't auto-switch if subscription is not active
    }
    
    dispatch(
      switchProfile({
        type: "business",
        businessId: businessData._id,
        primaryLocationId: businessData.primaryLocationId,
      })
    );
  }, [location.pathname, user, selectedBusinessId, profileType, dispatch]);

  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);

  const handleNotificationMenuOpen = (event) =>
    setNotificationAnchorEl(event.currentTarget);
  const handleNotificationMenuClose = () => setNotificationAnchorEl(null);

  // Fetch only first page for badge count to avoid N API calls on every load
  const fetchUnreadNotificationsCount = async (signal, page = 1, limit = 50) => {
    const res = await axiosInstance.get(ApiEndPoints.NOTIFICATION.list, {
      signal,
      params: { page, limit },
    });

    const notifications = res?.data?.data?.notifications || [];
    return notifications.filter((n) => !n.isRead).length;
  };

  // Fetch unread notification count for badge (only when user is logged in)
  const { data: notificationsCountData = 0 } = useQuery(
    ["notifications-count"],
    async ({ signal }) => {
      return await fetchUnreadNotificationsCount(signal);
    },
    {
      enabled: isAuthenticated,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    }
  );

  const unreadNotificationCount = notificationsCountData || 0;

  // Listen for unread conversations count from socket
  useEffect(() => {
    const handleUnreadConversationsCount = (data) => {
      if (data && typeof data.count === "number") {
        setUnreadConversationsCount(data.count);
      }
    };

    const unsubscribe = socketService.addEventListener(
      "unreadConversationsCount",
      handleUnreadConversationsCount
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const handleSwitchAccountOpen = (event) =>
    setSwitchAccountAnchorEl(event.currentTarget);
  const handleSwitchAccountClose = () => setSwitchAccountAnchorEl(null);

  const handleLogout = () => {
    // Disconnect socket before logout
    socketService.disconnect();
    dispatch(logout());
    toast.success(t("auth:loggedOut"));
    handleProfileMenuClose();
    navigate("/home");
    // Refresh the page to reload home page data
    window.location.reload();
  };

  const handleSidebarToggle = () => {
    if (shouldUseDialog) {
      setDialogOpen(!dialogOpen);
    } else {
      const newState = !sidebarExpanded;
      setSidebarExpanded(newState);
      if (onSidebarToggle) {
        onSidebarToggle(newState);
      }
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // const handleDarkModeToggle = () => {
  //   setDarkMode(!darkMode);
  // };

  const handleProfileSwitch = (type, business = null) => {
    // Check if switching to a different profile
    const isDifferentProfile = 
      type !== profileType ||
      (type === "business" && business?._id !== selectedBusinessId);

    if (!isDifferentProfile) {
      handleSwitchAccountClose();
      return;
    }

    // Handle business profile switching with subscription check
    if (type === "business" && business) {
      // Check isActive first - if business is inactive, show deactivation dialog
      const isBusinessActive = business.isActive !== false; // Default to true if not set
      
      if (!isBusinessActive) {
        // Show deactivation dialog if business is inactive
        dispatch(
          setShowBusinessDeactivationDialog({
            show: true,
            businessId: business._id,
          })
        );
        handleSwitchAccountClose();
        return;
      }
      
      // Check subscription status - if subscription is inactive, show subscription dialog
      const isSubscriptionActive = 
        business.subscriptionStatus === "active" ||
        business.subscriptionStatus === "trial" ||
        business.subscriptionStatus === "suspended";
      
      if (isSubscriptionActive) {
        // Switch profile and navigate
        dispatch(
          switchProfile({
            type: "business",
            businessId: business._id,
            primaryLocationId: business.primaryLocationId,
          })
        );
        handleSwitchAccountClose();
        navigate(`/business-dashboard/${business.primaryLocationId}`);
      } else {
        // Show subscription dialog if subscription is inactive
        dispatch(
          setShowSubscriptionPlanDialog({
            show: true,
            businessId: business._id,
            currentPlan: business?.subscriptionPlan?.planName || business?.planName || null,
          })
        );
        handleSwitchAccountClose();
        return;
      }
    } else {
      // Handle personal profile switching
      dispatch(
        switchProfile({
          type: "personal",
          businessId: null,
          primaryLocationId: null,
        })
      );
      handleSwitchAccountClose();
      navigate(`/profile/${user?.userId || ""}`);
    }
  };

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <StyledAppBar
          elevation={0}
          position="fixed"
          topOffset={topOffset}
          sx={{ pr: "0 !important" }}
        >
          <Toolbar sx={{ minHeight: { sm: 56, xs: 64 }, px: { sm: 6, xs: 0 } }}>
            {/* LEFT SIDE - Logo */}
            <LogoBox>
              {!isXsScreen && (
                <>
                  <Box
                    height={"100%"}
                    display="flex"
                    alignItems="center"
                    bgcolor={"#F2E9FF"}
                    borderRadius={"50px"}
                    p={1.5}
                  >
                    <NameLogo size={20} fill="#fff" />
                  </Box>
                  <IconLogo size={63} fill="#fff" />
                  {isAuthenticated && <DividerVertical />}
                </>
              )}
              {isAuthenticated && (
                <IconButton
                  color="inherit"
                  onClick={handleSidebarToggle}
                  sx={{ p: 0.5 }}
                  aria-label={t("dashboard:toggleSidebar")}
                  size="large"
                >
                  <MenuIcon style={{ fontSize: 26 }} />
                </IconButton>
              )}
            </LogoBox>

            {/* CENTER - Navigation Icons (only for authenticated) */}
            {isAuthenticated && (
              <CenterMenuBox>
              {menuItems.map((item, idx) => {
                // Show badge for chat icon if there are unread conversations
                const showBadge = item.label === "chat-vol2" && unreadConversationsCount > 0;
                
                return (
                  <Tooltip key={item.label} title={t(item.label)} arrow>
                    <IconButton
                      onClick={() =>
                        navigate(
                          typeof item.path === "function"
                            ? item.path(user)
                            : item.path
                        )
                      }
                      color="inherit"
                      sx={{
                        border: "1px solid transparent",
                        borderRadius: 3,
                        px: 0.5,
                        transition: "background 0.2s, border 0.2s",
                      }}
                      aria-label={t(item.label)}
                    >
                      {showBadge ? (
                        <Badge
                          badgeContent={unreadConversationsCount}
                          color="error"
                          sx={{
                            "& .MuiBadge-badge": {
                              fontSize: "0.7rem",
                              minWidth: 18,
                              height: 18,
                            },
                          }}
                        >
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </IconButton>
                  </Tooltip>
                );
              })}
              </CenterMenuBox>
            )}

            {/* Spacer for logged-out state */}
            {!isAuthenticated && <Box sx={{ flexGrow: 1 }} />}

            {/* RIGHT SIDE - Actions */}
            {isAuthenticated && (
              <RightMenuBox>
              <LanguageDropdown settings={settings} saveSettings={saveSettings} />
              <Tooltip title={t("dashboard:switchAccount")} arrow>
                <IconButton
                  color="inherit"
                  sx={{ p: 0 }}
                  onClick={handleSwitchAccountOpen}
                >
                  <ExchangeCircle size={22} />
                </IconButton>
              </Tooltip>

              <Popover
                open={Boolean(switchAccountAnchorEl)}
                anchorEl={switchAccountAnchorEl}
                onClose={handleSwitchAccountClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    width: 320,
                    boxShadow: 3,
                    borderRadius: 2,
                    p: "0px",
                  },
                }}
              >
                <Box p={"16px 16px 0px 16px"}>
                  <Typography
                    color="#2d3748"
                    fontWeight={"bold"}
                    sx={{ mb: 2, px: 1 }}
                  >
                    {t("dashboard:switchAccount")}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      px: 1,
                      color: "#2d3748",
                      textTransform: "lowercase",
                    }}
                  >
                    {t("dashboard:personalProfiles")}
                  </Typography>
                </Box>
                <Box sx={{ mt: 1, px: 2, pb: 1 }}>
                  {user && (
                    <Box
                      sx={{
                        mb: 1,
                        bgcolor: profileType === "personal" ? "rgba(108, 0, 230, 0.08)" : "#F3F5F9",
                        borderRadius: 1,
                        border: profileType === "personal" ? "1px solid #6C00E6" : "1px solid transparent",
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleProfileSwitch("personal")}
                        sx={{
                          borderRadius: 1,
                          "&:hover": { bgcolor: "transparent" },
                        }}
                      >
                        <Avatar
                          src={getImageDisplayUrl(user?.profilePic)}
                          sx={{ width: 28, height: 28, mr: 3 }}
                        />
                        <ListItemText
                          primary={user?.fullName || user?.name}
                          primaryTypographyProps={{
                            fontSize: 15,
                            fontWeight: profileType === "personal" ? 600 : 400,
                            color: profileType === "personal" ? "#6C00E6" : "#333",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        />
                      </ListItemButton>
                    </Box>
                  )}
                </Box>
                <Divider />

                <Typography
                  variant="caption"
                  sx={{
                    px: 3,
                    color: "#2d3748",
                    textTransform: "lowercase",
                    display: "block",
                  }}
                >
                  {t("dashboard:businessProfileSection")}
                </Typography>
                {user?.employee && user.employee.length > 0 ? (
                  <Box sx={{ px: 2, pb: 1 }}>
                    {user?.employee?.map((employee, index) => {
                      // Normalize employee data to match business structure
                      const businessData = {
                        _id: employee?.businessId?._id || employee?._id,
                        businessName: employee?.businessName || t("dashboard:businessProfile"),
                        primaryLocationId: employee?.primaryLocationId || employee?.locationIds?.[0]?._id || "",
                        businessProfilePic: employee?.businessProfilePic || employee?.businessId?.businessProfilePic,
                        isApprove: employee?.isApprove ?? employee?.businessId?.isApprove ?? 0,
                        isDocumentVerified: employee?.isDocumentVerified ?? employee?.businessId?.isDocumentVerified ?? -1,
                        subscriptionStatus: employee?.businessId?.subscriptionStatus || employee?.subscriptionStatus || "inactive",
                        subscriptionPlan: employee?.subscriptionPlan || employee?.businessId?.subscriptionPlan,
                        isActive: employee?.businessId?.isActive ?? true, // Add isActive field from businessId
                      };

                      const isSelected = profileType === "business" && selectedBusiness?._id === businessData._id;

                      return (
                        <Box
                          key={businessData._id}
                          sx={{
                            mb: 1,
                            bgcolor: isSelected ? "rgba(108, 0, 230, 0.08)" : "#F3F5F9",
                            borderRadius: 1,
                            border: isSelected ? "1px solid #6C00E6" : "1px solid transparent",
                          }}
                        >
                          <ListItemButton
                            onClick={() =>
                              handleProfileSwitch("business", businessData)
                            }
                            sx={{
                              borderRadius: 1,
                              "&:hover": { bgcolor: "transparent" },
                            }}
                          >
                            <Avatar
                              src={files(businessData?.businessProfilePic, "image")}
                              sx={{ width: 28, height: 28, mr: 3 }}
                            />
                            <ListItemText
                              primary={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {businessData?.businessName}
                                  </Typography>
                                  <ApprovIcon
                                    color={
                                      businessData?.isApprove === 1 &&
                                      businessData?.isDocumentVerified === 1
                                        ? "#0d6efd"
                                        : "#ffc107"
                                    }
                                    size={20}
                                    sx={{ flexShrink: 0 }}
                                  />
                                </Box>
                              }
                              primaryTypographyProps={{
                                fontSize: 15,
                                fontWeight: isSelected ? 600 : 400,
                                color: isSelected ? "#6C00E6" : "#333",
                              }}
                            />
                          </ListItemButton>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      mt: 1,
                      p: 3,
                      bgcolor: "#F8F9FA",
                      borderRadius: 1,
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: 12 }}
                    >
                      {t("dashboard:noBusinessAccounts")}
                    </Typography>
                  </Box>
                )}
              </Popover>
              <Tooltip title={t("dashboard:userProfile")} arrow>
                <IconButton
                  color="inherit"
                  aria-label={t("dashboard:userProfile")}
                  onClick={handleProfileMenuOpen}
                  sx={{ p: 0 }}
                >
                  <ArrowDropDownCircleOutlinedIcon sx={{ fontSize: 26 }} />
                </IconButton>
              </Tooltip>

              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleProfileMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 250,
                    boxShadow: 3,
                    borderRadius: 1,
                    p: "20px",
                    bgcolor: "#fff",
                  },
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="start"
                  gap={3}
                >
                  <Box display="flex" gap={2}>
                    <Avatar
                      src={getImageDisplayUrl(user?.profilePic)}
                      alt={user?.fullName || user?.name || ""}
                      sx={{ width: 40, height: 40, mb: 1 }}
                    />
                    <Box display="flex" flexDirection="column">
                      <Typography fontSize={14} fontWeight={600}>
                        {user?.fullName || user?.name || "User"}
                      </Typography>
                      <Typography
                        fontSize={12}
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {user?.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleLogout}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#6C00E6",
                      color: "#fff",
                      borderRadius: 1,
                      fontWeight: 600,
                      boxShadow: "none",
                      fontSize: 12,
                    }}
                  >
                    {t("auth:logout")}
                  </Button>
                </Box>
              </Popover>

              <Tooltip title={t("dashboard:notifications")} arrow>
                <IconButton
                  color="inherit"
                  aria-label={t("dashboard:notifications")}
                  size="large"
                  sx={{ p: 0 }}
                  onClick={handleNotificationMenuOpen}
                >
                  <Badge
                    badgeContent={unreadNotificationCount}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.7rem",
                        minWidth: 18,
                        height: 18,
                      },
                    }}
                  >
                    <NotificationsIcon sx={{ fontSize: 24 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
              <NotificationDropdown
                anchorEl={notificationAnchorEl}
                onClose={handleNotificationMenuClose}
              />
              </RightMenuBox>
            )}

            {/* Links and Login Button (only for logged-out users) */}
            {!isAuthenticated && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LanguageDropdown settings={settings} saveSettings={saveSettings} />
                {/* Business Link with Round Icon */}
                <Button
                  onClick={() => {
                    window.location.href = '/business';
                  }}
                  startIcon={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#fff',
                        borderRadius: '50%',
                        width: 28,
                        height: 28,
                        padding: '6px',
                      }}
                    >
                      <NameLogo size={16} fill="#6C00E6" />
                    </Box>
                  }
                  sx={{
                    color: "#fff",
                    fontWeight: 500,
                    textTransform: "none",
                    fontSize: "14px",
                    px: 2,
                    py: 0.75,
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  business
                </Button>

                {/* Contact Us Link with Support Icon */}
                <Button
                  onClick={() => {
                    window.location.href = '/business/contact';
                  }}
                  startIcon={<SupportAgentIcon />}
                  sx={{
                    color: "#fff",
                    fontWeight: 500,
                    textTransform: "none",
                    fontSize: "14px",
                    px: 2,
                    py: 0.75,
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  contact us
                </Button>

                {/* Login Button - Rounded Pill Style */}
                <Button
                  variant="contained"
                  onClick={() => openLoginModal()}
                  endIcon={<ArrowDropDownCircleOutlinedIcon sx={{ transform: 'rotate(230deg)' }} />}
                  sx={{
                    backgroundColor: "#fff",
                    color: "#6C00E6",
                    fontWeight: 300,
                    textTransform: "none",
                    borderRadius: "50px",
                    px: 3,
                    py: 1,
                    fontSize: "14px",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  log in
                </Button>
              </Box>
            )}
          </Toolbar>
        </StyledAppBar>

        {!shouldUseDialog && (
          <Sidebar isExpanded={sidebarExpanded}>
            <SidebarContent>
              <SidebarMenuContent 
                key={`${profileType}-${selectedBusinessId || 'personal'}`}
                user={user} 
                onItemClick={isXsScreen ? () => {
                  setSidebarExpanded(false);
                  if (onSidebarToggle) onSidebarToggle(false);
                } : () => {}} 
              />
            </SidebarContent>
          </Sidebar>
        )}

        <StyledDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          TransitionProps={{
            timeout: 300,
          }}
        >
          <DialogHeader>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              {t("dashboard:menu")}
            </Typography>
            <IconButton
              onClick={handleDialogClose}
              sx={{
                color: "#666",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogHeader>
          <DialogContent
            sx={{
              p: 3,
              pr: { sm: 1, xs: 3 },
              height: "100vh",
              overflow: "auto",
            }}
          >
            <SidebarMenuContent 
              key={`${profileType}-${selectedBusinessId || 'personal'}`}
              onItemClick={handleDialogClose} 
              user={user} 
            />
          </DialogContent>
        </StyledDialog>
      </Box>
    </>
  );
};

export default PublicLayoutAppBar;
